// Score-aggregation transform ported from data_centrifuge/vectors.coffee
// (calcData, seedEmptyGroups, and the calcOverallOutlierScore / calcTotals /
// calcGroups helpers it depends on). This is the data half of the legacy
// `page.classes.DataCentrifuge.Vectors` lib — the taxonomy half lives in
// ./vectors. Together they replace the CoffeeScript Vectors object.
//
// The `_surveyConfig` (survey.config.dcQuarantine) and `_features`
// (page.model.features.stat_data_cleaning_run_auto_quarantine.disabled) params used to drive the
// client-side default-quarantine decision. That assignment is now disabled — the wire status is
// authoritative — so both params are currently unused; they stay in the signature (and the gate is
// kept commented below) so re-enabling is a one-line change. Debug logging is intentionally dropped.

import {
  VECTORS_RAW,
  VECTORS_GROUPS,
  VECTORS_NAMES,
  getExcludedVectors,
  getParentGroup,
  getSuspicionExcludedVectors
} from '@aytm/app/stats/page/data-centrifuge/lib/vectors'

// Aggregate outlier score for one response's dataCleaning hash. A parent id in
// suspicion_exclude drops its whole children sum regardless of the user toggle;
// leaves in the list are not re-checked here (they ship seeded into disabledVectors).
// Optionally accepts a precomputed groupById (calcData already has one).
export const calcOverallOutlierScore = (dc, disabledVectors = [], groupById = null) => {
  let resolvedGroupById = groupById
  if (!resolvedGroupById) {
    resolvedGroupById = {}
    VECTORS_GROUPS.forEach(g => {
      if (g.vectors.length > 0) resolvedGroupById[g.id] = g
    })
  }

  const disabledLookup = {}
  disabledVectors.forEach(id => {
    disabledLookup[id] = true
  })

  const suspicionExcludedVectors = getSuspicionExcludedVectors()

  let overallOutlierScore = VECTORS_RAW.reduce((acc, cur) => {
    if (disabledLookup[cur] || suspicionExcludedVectors.includes(cur)) return acc

    const group = resolvedGroupById[cur]
    if (group) {
      return (
        acc +
        group.vectors.reduce(
          (sum, child) =>
            sum + ((!disabledLookup[child] && !suspicionExcludedVectors.includes(child) && dc[child]) || 0),
          0
        )
      )
    }
    return acc + (dc[cur] || 0)
  }, 0)

  if (overallOutlierScore > 3) overallOutlierScore = 4
  else if (overallOutlierScore > 2) overallOutlierScore = 3
  else if (overallOutlierScore > 1) overallOutlierScore = 2
  else if (overallOutlierScore > 0.00001) overallOutlierScore = 1
  else overallOutlierScore = 0

  return overallOutlierScore
}

// Survey-wide per-vector totals. Mutates `totals` in place (matches the coffee).
export const calcTotals = (dc, totals) =>
  VECTORS_RAW.reduce((acc, cur) => {
    if (acc[cur] == null) acc[cur] = 0

    const group = VECTORS_GROUPS.find(item => item.id === cur && item.vectors.length > 0)
    if (group) {
      acc[cur] += group.vectors.reduce((_acc, _cur) => _acc + (dc[_cur] || 0), 0)
    } else {
      acc[cur] += dc[cur] || 0
    }

    return acc
  }, totals)

// Per-group child rollups for one response. Mutates `groups` in place.
export const calcGroups = (uid, dc, groups) => {
  const excluded = getExcludedVectors()

  return VECTORS_GROUPS.reduce((acc, cur) => {
    if (cur.vectors.some(item => Object.hasOwn(dc, item) && !excluded.includes(item))) {
      acc[cur.id] = cur.vectors.reduce((_acc, _cur, _i) => {
        if (Object.hasOwn(dc, _cur) && !excluded.includes(_cur)) {
          if (_acc[_cur] == null) _acc[_cur] = { text: cur.names[_i], total: 0, scores: [] }
          if (!dc[_cur]) return _acc

          _acc[_cur].total += 1
          _acc[_cur].scores.push(uid)
        }
        return _acc
      }, acc[cur.id] || {})
    }
    return acc
  }, groups)
}

// Seed empty group entries into componentData.groups so the hierarchy renders
// even without data. Mutates `groups` in place and returns it.
export const seedEmptyGroups = groups => {
  const excluded = getExcludedVectors()

  VECTORS_GROUPS.forEach(group => {
    if (groups[group.id] == null) groups[group.id] = {}
    group.vectors.forEach((vector, i) => {
      if (excluded.includes(vector)) return
      if (groups[group.id][vector] == null) {
        groups[group.id][vector] = { text: group.names[i], total: 0, scores: [] }
      }
    })
  })
  return groups
}

// Positive weights of the suspicion-excluded leaves that fired on one response. These contribute
// nothing to the aggregate, so a response carrying only them is dropped before any surface can
// count it — this is what the Advisory tier reinstates.
export const calcAdvisoryLeaves = (dc, suspicionExcludedLookup) => {
  const leaves = {}
  Object.keys(suspicionExcludedLookup).forEach(leafId => {
    const value = dc[leafId] || 0
    if (value > 0) leaves[leafId] = value
  })
  return leaves
}

// Every explanation on the response, keyed by the row that renders it — a child's text rolls up to its
// parent vector, a raw-level or unplaced key stays on itself, and several entries concatenate into one.
// A parent-keyed entry appends like a child's rather than replacing what its children contributed
// — the two are text about the same row, and whichever arrives second must not erase the first.
const rollUpExplanations = (explanationData, rawLookup) => {
  const explanations = {}
  const append = (key, value) => {
    if (explanations[key] == null) explanations[key] = ''
    if (explanations[key].length > 0) explanations[key] += '<br />'
    explanations[key] += value
  }

  Object.entries(explanationData).forEach(([vectorId, value]) => {
    // A key the taxonomy does not place keys to itself rather than being dropped — every
    // explanation the producer wrote for this response has to reach a row.
    append(rawLookup[vectorId] ? vectorId : getParentGroup(vectorId) || vectorId, value)
  })

  // The producer writes its entries in whatever order it scored them, and the card renders one row
  // per key — so the canonical taxonomical order has to be imposed here rather than inherited.
  // Unplaced keys rank last and keep their arrival order (sort is stable).
  const rank = key => {
    const index = VECTORS_RAW.indexOf(key)
    return index >= 0 ? index : VECTORS_RAW.length
  }
  return Object.fromEntries(Object.entries(explanations).sort(([a], [b]) => rank(a) - rank(b)))
}

// Main transform: raw /raw response → componentData. Mirrors vectors.coffee:221.
// NOTE: mutates `cs.status` in place for auto-rejected responses — the coordinator
// relies on this side effect (the @data.cs items carry the updated status).
export const calcData = (response, disabledVectors = [], _features = {}, _surveyConfig = null) => {
  const data = {
    vectorIds: [],
    vectorNames: [],
    scores: [],
    groups: {},
    answerScores: {},
    suspicionScores: {},
    excludedScores: [],
    responses: {},
    scoresUIDs: [],
    quarantineUIDs: [],
    // Per-vector projected score arrays — "what this disabled vector WOULD flag".
    disabledVectorsScores: {},
    // Advisory tier: responses flagged only by suspicion-excluded leaves. Leaf-keyed so a
    // consumer can count parent rows and child rows from the same map.
    advisoryUIDs: [],
    advisoryScores: {},
    // Parents that appear only because an Advisory response hit them. The chart hides these so
    // the tier adds no axis; everything else treats them as ordinary vectors.
    advisoryOnlyVectorIds: []
  }

  if (Object.hasOwn(response, 'dcStatuses')) data.dcStatuses = response.dcStatuses

  if (Object.hasOwn(response, 'dcLastRunTs')) data.dcLastRunTs = response.dcLastRunTs
  if (Object.hasOwn(response, 'dcAnalyzedCount')) data.dcAnalyzedCount = response.dcAnalyzedCount
  if (Object.hasOwn(response, 'notAnalyzedCount')) data.notAnalyzedCount = response.notAnalyzedCount

  if (!response?.cs) return data

  // Client-side auto-quarantine is disabled — the wire status is authoritative. The gate that used
  // to drive it is kept here (commented) so re-enabling is a one-line change:
  //   let dcQuarantineEnabled = (Object.hasOwn(_surveyConfig || {}, 'dcQuarantine') && _surveyConfig.dcQuarantine) || false
  //   dcQuarantineEnabled ||= (Object.hasOwn(response, 'dcQuarantine') && response.dcQuarantine) || false
  //   // Default quarantine ON for surveys that have never run DC, unless an admin opted out via the
  //   // auto-quarantine feature flag. Tests the VALUE, not key presence: the report payload always
  //   // carries the timestamp key (null when never run), so a key-presence check never fires.
  //   dcQuarantineEnabled ||=
  //     (!response.dcLastRunTs && !_features?.stat_data_cleaning_run_auto_quarantine?.disabled) || false

  const groupById = {}
  VECTORS_GROUPS.forEach(group => {
    if (group.vectors.length > 0) groupById[group.id] = group
  })

  const rawLookup = {}
  VECTORS_RAW.forEach(id => {
    rawLookup[id] = true
  })

  const disabledVectorsLookup = {}
  disabledVectors.forEach(id => {
    disabledVectorsLookup[id] = true
  })

  const suspicionExcludedLookup = {}
  getSuspicionExcludedVectors().forEach(id => {
    suspicionExcludedLookup[id] = true
  })

  // Per-disabled-vector projection setup — response-independent, so build it once.
  const disabledVectorProjections = disabledVectors.map(disabledVectorId => {
    data.disabledVectorsScores[disabledVectorId] = []
    const group = groupById[disabledVectorId]
    if (group) {
      // Group parent: filter excludes the parent and all its children.
      const filteredDisabled = disabledVectors.filter(id => id !== disabledVectorId && !group.vectors.includes(id))
      return { disabledVectorId, group, filteredDisabled }
    }
    // Leaf: also exclude its parent group (if any) so the score can register.
    const parentGroupId = getParentGroup(disabledVectorId)
    const filteredDisabled = parentGroupId
      ? disabledVectors.filter(id => id !== disabledVectorId && id !== parentGroupId)
      : disabledVectors.filter(id => id !== disabledVectorId)
    return { disabledVectorId, group: null, filteredDisabled }
  })

  const totals = {}
  const scoringVectorIds = new Set()

  // Single pass: classify each response and, for scoring responses, also build the
  // score row, per-response detail, and per-disabled-vector projections.
  data.scores = response.cs.reduce((acc, cs) => {
    // Rows arrive in two shapes: a nested `{ response: { uid } }` or a flat `{ uid }` (a bare
    // stat-uid string). Read either; `??` keeps a legitimate 0 uid over the flat fallback. The
    // flat stat-uid becomes an SVG node id downstream, so map id-invalid chars (`@`, `.`) to `_`.
    const uid = cs.response?.uid ?? cs.uid?.replace(/[^\w-]/g, '_')
    const dataCleaning = cs.extra?.dataCleaning
    if (!dataCleaning) {
      if (['dc-auto-rejected', 'dc-restoring'].includes(cs.status)) data.quarantineUIDs.push(uid)
      data.excludedScores.push(uid)
      return acc
    }

    const overallOutlierScore = calcOverallOutlierScore(dataCleaning, [], groupById)

    // Client-side auto-quarantine disabled; wire status is authoritative:
    // if (overallOutlierScore >= 3 && !cs.restored && dcQuarantineEnabled) cs.status = 'dc-auto-rejected'

    if (['dc-auto-rejected', 'dc-restoring'].includes(cs.status)) data.quarantineUIDs.push(uid)

    // Advisory is decided against the CURRENT selection, not once at parse time: a response is
    // Advisory whenever everything it still shows is a suspicion-excluded vector. That covers the
    // response nothing else ever flagged, and equally the response whose scoring vectors the
    // reader has switched off — without the second case, turning on only an excluded vector made
    // its own co-occurring responses vanish instead of surfacing under it.
    const recordAdvisory = advisoryLeaves => {
      data.advisoryUIDs.push(uid)
      data.advisoryScores[uid] = advisoryLeaves
      data.answerScores[uid] = cs.answer
      data.responses[uid] = {
        status: cs.status,
        explanations: rollUpExplanations(cs.extra.dataCleaningExplanation || {}, rawLookup)
      }
    }

    if (overallOutlierScore < 1) {
      const advisoryLeaves = calcAdvisoryLeaves(dataCleaning, suspicionExcludedLookup)
      if (Object.keys(advisoryLeaves).length === 0) {
        data.excludedScores.push(uid)
        return acc
      }

      // Advisory hits feed the same survey-wide rollups as any other vector. Every toggle handler,
      // predicate and seed derives its vector ids from these two, so a vector missing from them is
      // invisible to the controls that are supposed to govern it.
      calcTotals(dataCleaning, totals)
      calcGroups(uid, dataCleaning, data.groups)

      recordAdvisory(advisoryLeaves)
      return acc
    }

    data.scoresUIDs.push(uid)

    // Survey-wide rollups (independent of user's disabledVectors). Both mutate in place.
    calcTotals(dataCleaning, totals)
    calcGroups(uid, dataCleaning, data.groups)

    VECTORS_RAW.forEach(id => {
      const group = groupById[id]
      const hit = group ? group.vectors.some(childId => (dataCleaning[childId] || 0) > 0) : (dataCleaning[id] || 0) > 0
      if (hit) scoringVectorIds.add(id)
    })

    const overallOutlierScoreFiltered = calcOverallOutlierScore(dataCleaning, disabledVectors, groupById)

    // Nothing scoring survives the selection: if an excluded vector still does, the response drops
    // to Advisory rather than off the surface entirely.
    if (overallOutlierScoreFiltered < 1) {
      const stillShowing = calcAdvisoryLeaves(dataCleaning, suspicionExcludedLookup)
      if (Object.keys(stillShowing).length > 0) recordAdvisory(stillShowing)
    }

    if (overallOutlierScoreFiltered >= 1) {
      // Masked per-vector weights — the sum that decides the score, so a user-disabled vector
      // contributes nothing. What the evidence card reports is driven by the explanations instead.
      const vectorScores = {}
      VECTORS_RAW.forEach(vectorId => {
        const group = groupById[vectorId]
        if (group) {
          let maskedSum = 0
          group.vectors.forEach(childVectorId => {
            if (!disabledVectorsLookup[childVectorId]) maskedSum += dataCleaning[childVectorId] || 0
          })
          vectorScores[vectorId] = disabledVectorsLookup[vectorId] ? 0 : maskedSum
        } else {
          vectorScores[vectorId] = disabledVectorsLookup[vectorId] ? 0 : dataCleaning[vectorId] || 0
        }
      })

      acc.push({ uid, vectorScores })

      data.answerScores[uid] = cs.answer
      data.suspicionScores[uid] = overallOutlierScoreFiltered

      if (data.responses[uid] == null) data.responses[uid] = { status: cs.status }
      data.responses[uid].explanations = rollUpExplanations(cs.extra.dataCleaningExplanation || {}, rawLookup)
    }

    // For each disabled vector, compute the response's outlier score AS IF only that
    // one vector were re-enabled. Feeds the projected counts the sidebar shows.
    disabledVectorProjections.forEach(projection => {
      const overallOutlierScoreProjected = calcOverallOutlierScore(dataCleaning, projection.filteredDisabled, groupById)
      if (overallOutlierScoreProjected < 1) return

      if (projection.group) {
        // Group parent: only count if at least one child has a positive value.
        if (projection.group.vectors.some(id => dataCleaning[id] > 0)) {
          data.disabledVectorsScores[projection.disabledVectorId].push([uid, overallOutlierScoreProjected])
        }
      } else if (dataCleaning[projection.disabledVectorId] > 0) {
        // Leaf: only count if the leaf itself contributes.
        data.disabledVectorsScores[projection.disabledVectorId].push([uid, overallOutlierScoreProjected])
      }
    })

    return acc
  }, [])

  VECTORS_RAW.forEach(id => {
    if (totals[id] > 0) {
      data.vectorIds.push(id)
      data.vectorNames.push(VECTORS_NAMES[id] || '')
      if (!scoringVectorIds.has(id)) data.advisoryOnlyVectorIds.push(id)
    }
  })

  // Project the {uid, vectorScores} rows into positional [uid, score, ...] arrays
  // using only the vector ids that contributed.
  data.scores = data.scores.map(({ uid, vectorScores }) => {
    const row = [uid]
    data.vectorIds.forEach(vectorId => row.push(vectorScores[vectorId]))
    return row
  })

  data.quarantineUIDs.sort()

  return data
}
